import { useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { ContentLayoutF } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { useSearchParams } from 'react-router-dom';

import { Header } from './components/header/Header';
import { ReturnDetailsCard } from './components/return-details-card/ReturnDetailsCard';
import { TimeWeightedReturns } from './components/time-weighted-returns/TimeWeightedReturns';
import { PerformanceAccountDocument } from './graphql/performanceAccount.generated';

import { DateRangeOption } from '../../graphql/schema';
import { ApplicationLayout } from '../../Shared/layout/ApplicationLayout';
import { urlBuilder } from '../../Shared/urlBuilder';
import { accountsByType } from '../shared/utils';
import { Balance } from '../user/components/balance/Balance';

export const AccountPerformancePage: React.FC = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.AllTime);

  const accountId = useRequiredParam('accountId');
  const [searchParams] = useSearchParams();
  const returnToParam = searchParams.get('returnTo') || urlBuilder.appPerformance.absolute();
  const returnLabelParam = searchParams.get('returnLabel') || 'All performance';

  const { data } = useGraphQLQuery(PerformanceAccountDocument, {
    variables: { accountIds: [accountId], dateRangeOption },
    throwOnError: true,
  });

  const accounts = data?.performance.accounts || [];

  const { investingAccounts } = accountsByType(accounts);
  const isInvestingAccount = investingAccounts.length > 0;

  return (
    <ErrorBoundary fallbackType="full" feature="performance" owner="investing_experience">
      <ApplicationLayout pageTitle="Performance">
        <ApplicationLayout.Header>
          {data && (
            <Header
              account={accounts[0]}
              dateRangeOption={dateRangeOption}
              setDateRangeOption={setDateRangeOption}
              returnToUrl={returnToParam}
              returnToLabel={returnLabelParam}
            />
          )}
        </ApplicationLayout.Header>
        <ApplicationLayout.Content>
          <ContentLayoutF>
            <ContentLayoutF.SlotContainer>
              <ContentLayoutF.Slot1
                css={{ display: 'grid', gap: { base: 'space.100', sm: 'space.300' }, gridAutoRows: 'max-content' }}
              >
                {data && (
                  <>
                    <Balance dateRangeOption={dateRangeOption} accounts={accounts} initialSelectedIds={[accountId]} />
                    {isInvestingAccount && (
                      <TimeWeightedReturns accountId={accountId} dateRangeOption={dateRangeOption} />
                    )}
                  </>
                )}
              </ContentLayoutF.Slot1>
              <ContentLayoutF.Slot2
                css={{
                  display: 'grid',
                  gap: { base: 'space.100', sm: 'space.300' },
                  gridAutoRows: 'max-content',
                  position: 'sticky',
                  top: 'space.400',
                  alignSelf: 'start',
                }}
              >
                <ReturnDetailsCard accountId={accountId} dateRangeOption={dateRangeOption} />
              </ContentLayoutF.Slot2>
            </ContentLayoutF.SlotContainer>
          </ContentLayoutF>
        </ApplicationLayout.Content>
      </ApplicationLayout>
    </ErrorBoundary>
  );
};
